import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import {
  ArrayOperators,
  SingleOperators,
} from 'common/src/query-params/operators';
import { GENERATED_ROUTES } from 'common/src/routes/GeneratedRoutes';
import { formatISODateOnly } from 'common/src/transformers/ISODateSerializer';
import type {
  ICreateBillDto,
  IUpdateBillDto,
} from 'common/src/types/bill.dtos';
import { IBill } from 'common/src/types/interfaces';

import {
  addBill,
  addBillImage,
  allocateMasterBill,
  deleteBill,
  deleteBillImage,
  getBills,
  updateBill,
  updateBillAllocations,
} from 'portal/src/api/BillsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import useAuth from 'portal/src/auth/useAuth';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import Stepper, { Step } from 'portal/src/components/common/Stepper';
import { ExtraButtonConfig } from 'portal/src/components/common/forms/FormButtonColumn';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import FormView from 'portal/src/components/common/forms/FormView';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { getFields } from './BillForm.fields';
import BillFormAccountInfo from './BillFormAccountInfo';
import { useBillFormContext } from './BillFormContext';
import BillFormHeader from './BillFormHeader';
import BillFormProvider from './BillFormProvider';
import SelectAccount from './SelectAccount';
import ViewAccountBills from './ViewAccountBills';

import { needsAllocationReview } from '../../Bills/helpers';
import CalculateAllocationsDialog from './Allocations/CalculateAllocationsDialog';
import EditAllocationLineItemsDialog from './Allocations/EditAllocationLineItemsDialog';
import BillLineItemsSection from './LineItemTable/BillLineItemsSection';

const BillFormInner = ({
  handleBackAction,
}: {
  handleBackAction: () => void;
}) => {
  const {
    account,
    accountId,
    activeAllocationTemplate,
    allocatedLineItemMappings: { isError: allocationMappingError },
    bill,
    billIsUsedInAllocations,
    generateNewBillLineItems,
    getBillPayload,
    getUpdateAllocationsPayload,
    headerForm: {
      control,
      formState: { isDirty, dirtyFields },
      handleSubmit,
      setValue,
      getValues,
      setError,
    },
    lineItems,
    previousBill: { isLoading: previousBillIsLoading },
    resetAllocationLineItems,
    setAccountId,
    setBill,
    total,
    validate,
  } = useBillFormContext();
  const client = useQueryClient();
  const snackbarHandler = useSnackbarHandler();
  const { hasSystemAdminAccess, hasReadOnlyAccess } = useAuth();
  const { getSnackbarApiErrorMessage } = useFormControlError({
    getValues,
    setError,
  });

  const [selectAccountFilter, setSelectAccountFilter] =
    useState<GridFilterModel>();
  const [selectAccountSort, setSelectAccountSort] = useState<GridSortModel>();
  const [skipDuplicateCheckOnce, setSkipDuplicateCheckOnce] = useState(false);
  const [activeStep, setActiveStep] = useState(accountId ? 1 : 0);

  const [openCalcAllocDialog, setOpenCalcAllocDialog] = useState(false);
  const [openEditAllocDialog, setOpenEditAllocDialog] = useState(false);
  const [openGenAllocDialog, setOpenGenAllocDialog] = useState(false);
  const [openAutoCreateAllocDialog, setOpenAutoCreateAllocDialog] =
    useState(false);
  const [openUpdateAllocDialog, setOpenUpdateAllocDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDuplicateBillDialog, setOpenDuplicateBillDialog] = useState(false);
  const [viewAccountBills, setViewAccountBills] = useState(false);

  const { mutateAsync: saveBillAsync, isPending: isSaveBillPending } =
    useMutation({
      mutationFn: async () => {
        const formValues = getValues();
        const file: File = formValues.workingImage;
        const { image, ...payload } = getBillPayload();

        if (bill.id) {
          await updateBill(bill.id, payload as IUpdateBillDto);

          if (dirtyFields?.workingImage) {
            file
              ? await addBillImage(bill.id, { file })
              : await deleteBillImage(bill.id);
          }

          return;
        }

        const id = await addBill(payload as ICreateBillDto);

        if (file && id) {
          await addBillImage(id, { file });
        }
      },
      onSuccess: () => {
        snackbarHandler.success(
          bill.id ? MESSAGES.BILL.UPDATED : MESSAGES.BILL.CREATED,
        );

        if (!bill.id) {
          setBill({} as IBill);
          setSkipDuplicateCheckOnce(false);
          setActiveStep(0);
          return;
        }

        handleBackAction();
      },
      onError: (err: unknown) => {
        snackbarHandler.error(getSnackbarApiErrorMessage(err));
      },
    });

  const { mutate: tryDeleteBill, isPending } = useMutation({
    mutationFn: async () => await deleteBill(bill.id),
    onSuccess: () => {
      handleBackAction();
      snackbarHandler.success(MESSAGES.BILL.DELETED);
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    },
    onSettled: () => closeDeleteDialog(),
  });

  const { mutate: markReviewed } = useMutation({
    mutationFn: async () => await updateBill(bill.id, { needsReview: false }),
    onSuccess: handleBackAction,
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    },
  });

  const {
    mutateAsync: generateAllocationsAsync,
    isPending: isGenerateAllocationPending,
  } = useMutation({
    mutationFn: async () => {
      await allocateMasterBill(bill.id);
    },
    onMutate: () => setOpenGenAllocDialog(false),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ALLOCATIONS.GENERATED);
      client.invalidateQueries({ queryKey: [QUERY_KEYS.detailPageData] });
    },
    onError: (e: any) => {
      let parsedError;
      try {
        parsedError = JSON.parse(e.message);
      } catch (_) {
        parsedError = {};
      }

      const message =
        parsedError.statusCode === 409
          ? parsedError.message
          : MESSAGES.GENERIC.ERROR;
      snackbarHandler.error(message);
      closeConfirmGenerateAllocDialog();
    },
  });

  const {
    mutateAsync: updateAllocationsAsync,
    isPending: isUpdateAllocationsPending,
  } = useMutation({
    mutationFn: async () => {
      await updateBillAllocations(bill.id, getUpdateAllocationsPayload());
    },
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ALLOCATIONS.UPDATED);
      resetAllocationLineItems();
      client.invalidateQueries({ queryKey: [QUERY_KEYS.detailPageData] });
    },
    onError: (e: any) => {
      let parsedError;
      try {
        parsedError = JSON.parse(e.message);
      } catch (_) {
        parsedError = {};
      }

      const message =
        parsedError.statusCode === 409
          ? parsedError.message
          : MESSAGES.GENERIC.ERROR;
      snackbarHandler.error(message);
      closeConfirmUpdateAllocDialog();
    },
  });

  async function handleVisitAllocatedBills(): Promise<void> {
    try {
      const filters = [
        {
          field: 'id',
          operator: ArrayOperators.IN,
          value: bill.allocatedBills.map(({ id }) => id),
        },
      ];
      window.open(
        `${GENERATED_ROUTES.ADMINISTRATION.BILLS}?filters=${JSON.stringify(filters)}`,
        '_blank',
      );
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }

  const requestsPending =
    isPending ||
    isSaveBillPending ||
    isGenerateAllocationPending ||
    isUpdateAllocationsPending;
  const lineItemsDirty = bill.lineItems !== lineItems;
  const isReadOnly = hasReadOnlyAccess();

  const showCalculateAllocations = !bill.isAllocated && account.isMaster;
  const showEditAllocations =
    !!bill.id && !!bill.isAllocated && account.isMaster;
  const showViewAllocations = !!bill.isAllocated;

  const buildBillActionButtons = (): ExtraButtonConfig[] => {
    const actionButtons: ExtraButtonConfig[] = [];

    actionButtons.push({
      label: 'View Account Bills',
      isDisabled: requestsPending,
      onClick: () => {
        if (accountId && !viewAccountBills) setViewAccountBills(true);
      },
    });

    if (showViewAllocations) {
      actionButtons.push({
        label: 'View Allocations',
        isDisabled: requestsPending,
        onClick: handleVisitAllocatedBills,
      });
    }

    if (showCalculateAllocations) {
      actionButtons.push({
        label: 'Calculate Allocations',
        isDisabled:
          !activeAllocationTemplate ||
          requestsPending ||
          billIsUsedInAllocations,
        onClick: () => {
          setOpenCalcAllocDialog(true);
        },
      });
    }

    if (showEditAllocations) {
      actionButtons.push({
        label: 'Edit Allocations',
        isDisabled:
          !activeAllocationTemplate ||
          allocationMappingError ||
          requestsPending ||
          isReadOnly,
        onClick: () => {
          setOpenEditAllocDialog(true);
        },
      });
    }

    if (bill.id && bill.needsReview && hasSystemAdminAccess()) {
      actionButtons.push({
        label: 'Mark Reviewed',
        isDisabled: requestsPending,
        onClick: () => {
          markReviewed();
        },
      });
    }

    if (bill.id) {
      actionButtons.push({
        label: 'Delete',
        isDisabled: bill?.account?.isSub || requestsPending || isReadOnly,
        onClick: () => setOpenDeleteDialog(true),
        color: 'error',
        variant: 'outlined',
      });
    }

    return actionButtons;
  };

  const billActionButtons = buildBillActionButtons();

  // A new bill's start date is prefilled from the previous bill, so block input until it loads
  const isPreviousBillLoading = !bill.id && previousBillIsLoading;

  const formFieldsConfig = getFields({
    setValue,
    getValues,
    id: bill?.id,
    showImageChangeMsg: () =>
      snackbarHandler.info('Bill image change ready to be saved with the bill'),
    billIsUsedInAllocations,
    imageWasSaved: !!bill?.image || !!bill?.statement?.imageLocation,
    billPageNumber: bill?.pageNumber,
    statementId: bill?.statement?.id,
    importFile: bill?.importFile,
    hasSystemAdminAccess: hasSystemAdminAccess(),
    isReadOnly,
    billMethod: bill?.billEntryMethod,
  }).map((field) => ({
    ...field,
    disabled: field.disabled || isPreviousBillLoading,
  }));

  const validateHeaderForm = async (): Promise<boolean> => {
    let isValidHeaderForm = false;

    await handleSubmit(
      () => {
        isValidHeaderForm = true;
      },
      () => {
        isValidHeaderForm = false;
      },
    )();

    return isValidHeaderForm;
  };

  const submitBill = async (
    onSave: () => Promise<unknown>,
  ): Promise<boolean> => {
    if (!(await validateHeaderForm())) {
      return false;
    }

    let nextFn: () => void = () => {
      void onSave();
    };

    if (!bill.id) {
      if (
        activeAllocationTemplate &&
        !activeAllocationTemplate.bypassAutomaticCreation
      ) {
        nextFn = () => setOpenAutoCreateAllocDialog(true);
      }
    }

    await validate(nextFn);
    return true;
  };

  const steps: Step[] = [
    {
      disableNext: !accountId,
      stepLabel: 'Select Bill Account',
      view: (
        <SelectAccount
          filterModel={selectAccountFilter}
          onDoubleClickSelection={() => {
            setActiveStep(1);
          }}
          onSelectionChange={(newAccountId) => {
            setBill({} as IBill);
            setAccountId(newAccountId);
          }}
          setFilterModel={setSelectAccountFilter}
          setSortModel={setSelectAccountSort}
          sortModel={selectAccountSort}
        />
      ),
    },
    {
      disableNext: !isDirty || isPreviousBillLoading,
      extraButtons: [
        {
          label: 'View Account Bills',
          onClick: () => {
            if (accountId && !viewAccountBills) setViewAccountBills(true);
          },
        },
      ],
      nextBtnCondition: async () => {
        if (skipDuplicateCheckOnce) {
          setSkipDuplicateCheckOnce(false);
          generateNewBillLineItems(); // Set line items step for new bill
          return true;
        }

        if (await validateHeaderForm()) {
          const { date, endDate, amount } = getValues();
          try {
            const {
              meta: { totalCount },
              data,
            } = await getBills({
              sort: 'date',
              filters: [
                {
                  field: 'date',
                  value: formatISODateOnly(date || endDate),
                  operator: SingleOperators.EQUALS,
                },
                {
                  field: 'amount',
                  value: amount,
                  operator: SingleOperators.EQUALS,
                },
                {
                  field: 'accountId',
                  value: accountId,
                  operator: SingleOperators.EQUALS,
                },
              ],
            });
            const openWarning = totalCount > 0 && data[0]?.id !== bill.id;
            if (openWarning) {
              setOpenDuplicateBillDialog(openWarning);
              return false;
            } else {
              generateNewBillLineItems();
            }
          } catch (err) {
            Logger.captureError(err as Error);
            snackbarHandler.error(MESSAGES.GENERIC.ERROR);
            return false;
          }
          return true;
        }
      },
      stepLabel: 'Enter Bill Information',
      view: (
        <FormView
          control={control}
          header={<BillFormHeader />}
          beforeMainFields={
            <BillFormAccountInfo
              account={account}
              billUsedInAllocation={false}
            />
          }
          mainFieldsConfig={formFieldsConfig}
        />
      ),
    },
    {
      disableNext: !(isDirty || lineItemsDirty),
      extraButtons: billActionButtons,
      nextBtnCondition: async () => submitBill(saveBillAsync),
      stepLabel: 'Enter Line Items & Submit Bill',
      view: (
        <FormView
          control={control}
          header={<BillFormHeader />}
          mainFieldsConfig={formFieldsConfig}
          beforeMainFields={
            <BillFormAccountInfo
              account={account}
              billUsedInAllocation={false}
            />
          }
          afterMainFields={<BillLineItemsSection />}
        />
      ),
    },
  ];
  const closeAutoCreateAllocDialog = () => setOpenAutoCreateAllocDialog(false);
  const closeConfirmGenerateAllocDialog = () => setOpenGenAllocDialog(false);
  const closeConfirmUpdateAllocDialog = () => setOpenUpdateAllocDialog(false);
  const closeDeleteDialog = () => setOpenDeleteDialog(false);
  const closeDuplicateBillDialog = () => setOpenDuplicateBillDialog(false);

  if (viewAccountBills && accountId) {
    return (
      <ViewAccountBills
        accountId={accountId}
        handleBackAction={() => setViewAccountBills(false)}
      />
    );
  }

  return (
    <>
      {bill.id ? (
        <FormContainer
          control={control}
          header={
            <BillFormHeader
              billId={bill.id}
              billEntryMethod={bill.billEntryMethod}
              correctedBy={bill.correctedBy}
              infoOnly={bill.infoOnly}
              infoOnlyReason={bill.infoOnlyReason}
              isCorrection={bill.isCorrection}
              isPaid={bill.isPaid}
              showWarningBadge={needsAllocationReview(bill)}
            />
          }
          extraButtons={billActionButtons}
          handleBackAction={handleBackAction}
          handleSubmit={async () => {
            await submitBill(saveBillAsync);
          }}
          isDirty={isDirty || lineItemsDirty}
          isNew={false}
          isPending={requestsPending}
          isSubmitDisabled={isReadOnly}
          beforeMainFields={
            <BillFormAccountInfo
              account={account}
              billUsedInAllocation={billIsUsedInAllocations}
            />
          }
          mainFieldsConfig={formFieldsConfig}
          afterMainFields={<BillLineItemsSection />}
        />
      ) : (
        <Stepper
          currentStep={activeStep}
          isPending={requestsPending}
          isUpdate={false}
          handleBackAction={handleBackAction}
          onStepChange={setActiveStep}
          steps={steps}
        />
      )}
      <ConfirmDialog
        open={openDuplicateBillDialog}
        onClose={closeDuplicateBillDialog}
        message={
          'Warning: a bill already exists with the same account number,' +
          ' bill date, and amount.'
        }
        primaryButtonLabel="Continue"
        onPrimaryButtonClicked={() => {
          closeDuplicateBillDialog();
          setSkipDuplicateCheckOnce(true);
        }}
        secondaryButtonLabel="Cancel"
        onSecondaryButtonClicked={closeDuplicateBillDialog}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
      />
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={closeDeleteDialog}
        message={
          'Are you sure you want to delete this bill' +
          `${bill.allocatedBills?.length ? ' with its allocations' : ''}` +
          '?'
        }
        primaryButtonLabel="Delete"
        onPrimaryButtonClicked={tryDeleteBill}
        secondaryButtonLabel="Cancel"
        onSecondaryButtonClicked={closeDeleteDialog}
        renderElement={<DeleteIcon sx={{ color: 'error.main' }} />}
        isLoading={isPending}
      />
      <ConfirmDialog
        open={openGenAllocDialog}
        onClose={closeConfirmGenerateAllocDialog}
        message={
          'Are you sure you want to generate allocations from this bill?'
        }
        primaryButtonLabel="Accept"
        onPrimaryButtonClicked={async () =>
          await submitBill(async () => {
            await generateAllocationsAsync();
          })
        }
        secondaryButtonLabel="Cancel"
        onSecondaryButtonClicked={closeConfirmGenerateAllocDialog}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
        isLoading={isGenerateAllocationPending}
      />
      <ConfirmDialog
        open={openUpdateAllocDialog}
        onClose={closeConfirmUpdateAllocDialog}
        message={'Are you sure you want to update allocations for this bill?'}
        primaryButtonLabel="Accept"
        onPrimaryButtonClicked={async () => {
          await updateAllocationsAsync();
        }}
        secondaryButtonLabel="Cancel"
        onSecondaryButtonClicked={closeConfirmUpdateAllocDialog}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
        isLoading={isUpdateAllocationsPending}
      />
      <ConfirmDialog
        open={openAutoCreateAllocDialog}
        onClose={closeAutoCreateAllocDialog}
        message={
          <Typography>
            We detect that an allocation has been configured for this account,
            therefore this bill will be allocated automatically.
            <br />
            <br />
            (If you would like to review your allocation configuration, go to
            the specific account located in the &apos;Accounts&apos; tab of the
            &apos;Administration&apos; section)
          </Typography>
        }
        primaryButtonLabel="Accept"
        secondaryButtonLabel="Cancel"
        onPrimaryButtonClicked={async () => {
          await saveBillAsync();
          closeAutoCreateAllocDialog();
        }}
        onSecondaryButtonClicked={closeAutoCreateAllocDialog}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
        isLoading={isSaveBillPending}
      />
      <CalculateAllocationsDialog
        billId={bill?.id}
        lineItemTotalDoesNotMatchAmount={total !== getValues('amount')}
        onClose={() => setOpenCalcAllocDialog(false)}
        onGenerateAllocations={() => {
          setOpenCalcAllocDialog(false);
          setOpenGenAllocDialog(true);
        }}
        open={openCalcAllocDialog}
      />
      <EditAllocationLineItemsDialog
        onClose={() => setOpenEditAllocDialog(false)}
        onSave={() => {
          setOpenEditAllocDialog(false);
          setOpenUpdateAllocDialog(true);
        }}
        open={openEditAllocDialog}
      />
      <Backdrop open={isGenerateAllocationPending} style={{ zIndex: 2 }}>
        <CircularProgress />
      </Backdrop>
    </>
  );
};

const BillForm = ({
  entity = {},
  handleBackAction,
}: {
  entity?: Partial<IBill>;
  handleBackAction: () => void;
}) => {
  const accountId = useSearchParams()[0].get('account') ?? '';

  return (
    <BillFormProvider
      defaultAccountId={accountId || entity?.accountId}
      defaultBill={entity}
    >
      <BillFormInner handleBackAction={handleBackAction} />
    </BillFormProvider>
  );
};

export default BillForm;
